import * as React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import {
  Check,
  Copy,
  MoreHorizontal,
  Plus,
  ShieldCheck,
  User as UserIcon,
  Wallet
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue
} from "@/components/ui/combobox";
import { DataTable } from "@/components/data-table/DataTable";
import { SortableHeader } from "@/components/data-table/SortableHeader";
import { type AppTableFeatures } from "@/components/data-table/features";
import AddUsersDialog from "@/components/AddUsersDialog";
import SegmentedTabs from "@/components/SegmentedTabs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { RefreshButton } from "@/components/RefreshButton";
import { toast } from "sonner";
import {
  ApiError,
  assignRole,
  fetchOrgUsers,
  revokeRole,
  userPhotoUrl
} from "../../api";
import { OrgUser } from "../../types";
import { roleLabel } from "@/lib/roles";
import { cn } from "@/lib/utils";

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const columnHelper = createColumnHelper<AppTableFeatures, OrgUser>();

/** Small copy-to-clipboard button shown beside a user's email. Hidden until
 *  the row is hovered (or the button is focused, for keyboard users) on
 *  pointer devices; always visible on touch, which has no hover. Swaps its
 *  copy icon for a tick for ~1.5s after a successful copy. */
function CopyEmailButton({ email }: { email: string }) {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy the email.");
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "h-5 w-5 shrink-0 text-muted-foreground transition-opacity",
        "opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100",
        copied && "md:opacity-100"
      )}
      aria-label={copied ? "Email copied" : `Copy ${email}`}
      onClick={copy}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-primary" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );
}

type RoleFilter = "All" | "Admin" | "Finance";

/** Distinct, non-empty values of one field across the loaded users, sorted -
 *  the options for the Department / Designation filters, so they only ever
 *  offer values that actually exist in the table. */
function distinctValues(
  users: OrgUser[],
  field: "department" | "designation"
): string[] {
  return Array.from(
    new Set(users.map((u) => u[field]).filter((v): v is string => !!v?.trim()))
  ).sort((a, b) => a.localeCompare(b));
}

/** Multi-select filter for a free-text field (Department / Designation).
 *  Selected values show as removable chips; an empty selection means "no
 *  filter" (all values pass). `placeholder` stands in for the old "All …"
 *  option - there's no explicit "All" item, clearing the chips is "All". */
function FieldFilter({
  value,
  onChange,
  options,
  placeholder,
  ariaLabel
}: {
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  placeholder: string;
  ariaLabel: string;
}) {
  // Anchor the popup to the trigger button (not useComboboxAnchor, which is
  // typed for the div-based chips container).
  const anchor = React.useRef<HTMLButtonElement | null>(null);
  // Noun for the count summary, derived from the placeholder ("All
  // departments" -> "department"/"departments").
  const noun = placeholder.replace(/^All\s+/i, "").replace(/s$/i, "");
  return (
    <Combobox
      multiple
      items={options}
      value={value}
      onValueChange={(v) => onChange(v as string[])}
    >
      {/* Trigger is a fixed-size button (matches the Select filters next to
         it) showing a count summary, so long values never grow or misalign
         the toolbar - the searchable list lives in the popup below. */}
      <ComboboxTrigger
        ref={anchor}
        aria-label={ariaLabel}
        className="flex h-9 w-full items-center justify-between gap-2 overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-sm data-[placeholder]:text-muted-foreground sm:w-[11.875rem] dark:bg-input/30"
      >
        <ComboboxValue>
          {(values: string[]) =>
            values.length === 0 ? (
              <span className="min-w-0 flex-1 truncate text-left text-muted-foreground">
                {placeholder}
              </span>
            ) : (
              // First selection as text + a "+N" badge for the rest, so the
              // trigger reads like a real chip rather than a bare count.
              <span className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
                <span className="truncate">{values[0]}</span>
                {values.length > 1 && (
                  <Badge
                    variant="secondary"
                    className="h-5 shrink-0 px-1.5 font-normal tabular-nums"
                  >
                    +{values.length - 1}
                  </Badge>
                )}
              </span>
            )
          }
        </ComboboxValue>
      </ComboboxTrigger>
      <ComboboxContent anchor={anchor}>
        <ComboboxInput
          placeholder={`Search ${noun}s…`}
          showTrigger={false}
        />
        <ComboboxEmpty>No matches.</ComboboxEmpty>
        <ComboboxList>
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {item}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

/** Same "All …" + options Select as FieldFilter, but for the Role column -
 *  kept as its own component since its options are a fixed {value, label}
 *  pair (role key vs. its display label from roleLabel()) rather than
 *  FieldFilter's plain string-in/string-out list. Desktop-only home for
 *  this filter - the Role column header's own FilterableHeader dropdown
 *  and the phone-only SegmentedTabs in toolbarStart already cover it
 *  elsewhere; this is a third, always-visible entry point next to
 *  Department/Designation so it doesn't only live inside the column
 *  header menu. */
function RoleFieldFilter({
  value,
  onChange
}: {
  value: RoleFilter;
  onChange: (value: RoleFilter) => void;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as RoleFilter)}>
      <SelectTrigger
        aria-label="Filter by role"
        className="h-9 w-full sm:w-[11.875rem]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="All">All roles</SelectItem>
        <SelectItem value="Admin">Admin</SelectItem>
        <SelectItem value="Finance">{roleLabel("Finance")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

/** Admin / Finance Approver badges for one user, as bare fragment children
 *  (no wrapping div) - Used by the Role column (desktop table, wrapped in
 *  its own flex container there) and, for phones, embedded directly inside
 *  the Designation field's own flex-wrap row instead of its own card slot,
 *  so each badge wraps independently alongside the designation text rather
 *  than the whole badge group wrapping down together as one block - see the
 *  "designation" and "role" column defs below. */
function RoleBadges({ roles }: { roles: string[] }) {
  if (roles.length === 0) {
    return (
      <Badge variant="outline" className="gap-1 font-normal">
        <UserIcon className="h-3 w-3" /> User
      </Badge>
    );
  }
  return (
    <>
      {roles.includes("Admin") && (
        <Badge className="gap-1 font-normal">
          <ShieldCheck className="h-3 w-3" /> Admin
        </Badge>
      )}
      {roles.includes("Finance") && (
        <Badge variant="secondary" className="gap-1 font-normal">
          <Wallet className="h-3 w-3 shrink-0" />
          {roleLabel("Finance")}
        </Badge>
      )}
    </>
  );
}

export default function UserManagement() {
  const [users, setUsers] = React.useState<OrgUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [addOpen, setAddOpen] = React.useState(false);
  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>("All");
  const [departmentFilter, setDepartmentFilter] = React.useState<string[]>([]);
  const [designationFilter, setDesignationFilter] = React.useState<string[]>(
    []
  );
  const [confirm, setConfirm] = React.useState<{
    user: OrgUser;
    role: string;
    grant: boolean;
  } | null>(null);
  const [confirmError, setConfirmError] = React.useState<string | null>(null);
  const [applying, setApplying] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    fetchOrgUsers()
      .then(setUsers)
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => {
    load();
  }, [load]);

  // How many Admins exist - `users` is the role-holder list (see
  // fetchOrgUsers's docstring), so this is accurate without a directory
  // fetch. Used to block removing the last Admin client-side, ahead of the
  // backend's own 409 (see db/roles_store.py's LastAdminError).
  const adminCount = React.useMemo(
    () => users.filter((u) => u.roles.includes("Admin")).length,
    [users]
  );

  const departments = React.useMemo(
    () => distinctValues(users, "department"),
    [users]
  );
  const designations = React.useMemo(
    () => distinctValues(users, "designation"),
    [users]
  );

  // A refresh can drop the last user with a selected value - prune any
  // selected options that no longer exist rather than filtering on them.
  React.useEffect(() => {
    setDepartmentFilter((prev) => {
      const next = prev.filter((v) => departments.includes(v));
      return next.length === prev.length ? prev : next;
    });
  }, [departments]);
  React.useEffect(() => {
    setDesignationFilter((prev) => {
      const next = prev.filter((v) => designations.includes(v));
      return next.length === prev.length ? prev : next;
    });
  }, [designations]);

  const fieldFiltersActive =
    departmentFilter.length > 0 || designationFilter.length > 0;

  const rows = React.useMemo(
    () =>
      users.filter(
        (u) =>
          (roleFilter === "All" || u.roles.includes(roleFilter)) &&
          (departmentFilter.length === 0 ||
            (!!u.department && departmentFilter.includes(u.department))) &&
          (designationFilter.length === 0 ||
            (!!u.designation && designationFilter.includes(u.designation)))
      ),
    [users, roleFilter, departmentFilter, designationFilter]
  );

  const applyConfirm = async () => {
    if (!confirm) return;
    const { user, role, grant } = confirm;
    setApplying(true);
    setConfirmError(null);
    try {
      if (grant) {
        await assignRole(user.id, role);
      } else {
        await revokeRole(user.id, role);
      }
      setConfirm(null);
      load();
      toast.success(
        grant
          ? `${roleLabel(role)} granted to ${user.displayName}`
          : `${roleLabel(role)} removed from ${user.displayName}`
      );
    } catch (err) {
      // 409 from assign_role's single-holder/already-assigned checks -
      // surface it instead of silently doing nothing (this used to swallow
      // every error).
      const message =
        err instanceof ApiError
          ? err.message
          : `Could not ${grant ? "grant" : "remove"} ${roleLabel(role)} ${grant ? "to" : "from"} ${user.displayName} - check your connection and try again.`;
      setConfirmError(message);
    } finally {
      setApplying(false);
    }
  };

  const columns = React.useMemo(
    () =>
      columnHelper.columns([
        // Name + email in the accessor so the table's search (a substring
        // match on this column's value - see DataTable) finds a user by
        // either, same as AuditLogs.tsx's actor column. Sorting still reads
        // name-first.
        columnHelper.accessor((row) => `${row.displayName || ""} ${row.email || ""}`, {
          id: "user",
          enableHiding: false,
          meta: { label: "User" },
          // The search box filters this column (filterColumn="user"), but the
          // accessor is only displayName - match the email too so searching by
          // email works as well as by name.
          filterFn: (row, _columnId, filterValue) => {
            const q = String(filterValue).toLowerCase().trim();
            if (!q) return true;
            const u = row.original;
            return (
              (u.displayName ?? "").toLowerCase().includes(q) ||
              (u.email ?? "").toLowerCase().includes(q)
            );
          },
          header: ({ column }) => (
            <SortableHeader column={column}>User</SortableHeader>
          ),
          cell: ({ row }) => {
            const u = row.original;
            return (
              <div className="group flex items-center gap-3">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={userPhotoUrl(u.id)} alt="" />
                  <AvatarFallback className="text-xs">
                    {initials(u.displayName || u.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="truncate font-medium">{u.displayName}</div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <span className="truncate">{u.email}</span>
                    <CopyEmailButton email={u.email} />
                  </div>
                </div>
              </div>
            );
          }
        }),
        // columnHelper.accessor("department", {
        //   id: "department",
        //   meta: { label: "Department" },
        //   header: ({ column }) => (
        //     <SortableHeader column={column}>Department</SortableHeader>
        //   ),
        //   cell: ({ row }) => row.original.department || "-"
        // }),
        columnHelper.accessor("designation", {
          id: "designation",
          // fullWidth: this field spans the whole phone card (not half, in
          // DataTable's two-column field grid) - the Admin/Finance Approver
          // badges always sit on their own line below the designation text,
          // so they need the full card width regardless of how long the
          // designation is (a shared line would put the badges in a
          // different spot row to row depending on text length).
          meta: { label: "Designation", fullWidth: true },
          header: ({ column }) => (
            <SortableHeader column={column}>Designation</SortableHeader>
          ),
          cell: ({ row }) => (
            // This same cell renderer also backs the desktop table's
            // Designation column (DataTable's cell/flexRender path is
            // shared by both layouts) - md:hidden keeps the badges
            // phone-only here, since desktop already shows them in its own
            // separate Role column.
            <div className="flex flex-col gap-1.5">
              <span>{row.original.designation || "-"}</span>
              <span className="flex flex-wrap items-center gap-1.5 md:hidden">
                <RoleBadges roles={row.original.roles} />
              </span>
            </div>
          )
        }),
        // columnHelper.display({
        //   id: "manager",
        //   meta: { label: "Reporting manager" },
        //   header: "Reporting manager",
        //   cell: ({ row }) => (
        //     <span className="text-sm">
        //       {row.original.reportingManagerName || "-"}
        //     </span>
        //   )
        // }),
        columnHelper.display({
          id: "role",
          enableHiding: false,
          // Hidden on phone cards, not "badge" - its content now renders
          // inside the Designation field's own cell instead (RoleBadges),
          // so Admin/Finance Approver sit in the same row as Designation
          // rather than at the top of the card next to the name. Desktop's
          // table keeps this as its own Role column, unaffected.
          meta: { label: "Role", mobile: "hidden" },
          header: "Role",
          cell: ({ row }) => (
            <div className="flex flex-wrap items-center gap-1.5">
              <RoleBadges roles={row.original.roles} />
            </div>
          )
        }),
        columnHelper.display({
          id: "actions",
          enableHiding: false,
          header: () => <div className="text-right">Actions</div>,
          cell: ({ row }) => {
            const u = row.original;
            const isAdmin = u.roles.includes("Admin");
            const isFinance = u.roles.includes("Finance");
            // Only relevant when revoking (not granting) - Admin has no cap,
            // but removing the last one would leave nobody who can grant it
            // back (see db/roles_store.py's LastAdminError).
            const isLastAdmin = isAdmin && adminCount <= 1;
            return (
              <div className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Manage access</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      disabled={isLastAdmin}
                      onClick={() =>
                        setConfirm({ user: u, role: "Admin", grant: !isAdmin })
                      }
                    >
                      {isLastAdmin
                        ? "Last Admin - assign another first"
                        : isAdmin
                          ? "Remove Admin"
                          : "Make Admin"}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        setConfirm({
                          user: u,
                          role: "Finance",
                          grant: !isFinance
                        })
                      }
                    >
                      {isFinance
                        ? `Remove ${roleLabel("Finance")}`
                        : `Make ${roleLabel("Finance")}`}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          }
        })
      ]),
    [adminCount]
  );

  return (
    <PageContainer fullWidth>
      <PageHeader
        title="User Management"
        description="Org users synced from Microsoft Entra ID. Admin and Finance Approver roles are configured here."
        titleActions={<RefreshButton onRefresh={load} isLoading={loading} />}
      />

      <DataTable
        columns={columns}
        data={rows}
        isLoading={loading}
        filterColumn="user"
        filterPlaceholder="Search name or email…"
        filterClassName="sm:max-w-[13.75rem]"
        persistKey="user-management"
        emptyMessage={
          fieldFiltersActive
            ? "No users match the selected filters."
            : roleFilter === "All"
              ? "No users found."
              : `No ${roleFilter === "Finance" ? roleLabel("Finance") : roleFilter} users found.`
        }
        toolbarStart={
          // The Role filter normally lives in its column header
          // (FilterableHeader) - phone cards have no headers, so it is
          // repeated here for phones only. min-w-0 flex-1 (not a fixed
          // width) so it shares the row with the Department filter
          // (toolbarAfterSearch) instead of claiming the whole row and
          // pushing Department onto a line of its own.
          <div className="order-first min-w-0 flex-1 md:hidden">
            <SegmentedTabs
              value={roleFilter}
              onChange={setRoleFilter}
              className="h-9 w-full"
              options={[
                { value: "All", label: "All roles" },
                { value: "Admin", label: "Admin" },
                { value: "Finance", label: roleLabel("Finance") }
              ]}
            />
          </div>
        }
        toolbarAfterSearch={
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
            {/* Phones already get the Role filter from the SegmentedTabs
               above (toolbarStart, md:hidden) - showing this Select too
               would double it up, so it's desktop/tablet-only here. */}
            <div className="hidden md:block">
              <RoleFieldFilter value={roleFilter} onChange={setRoleFilter} />
            </div>
            <div className="min-w-0 flex-1 sm:w-[11.875rem] sm:flex-none">
              <FieldFilter
                value={departmentFilter}
                onChange={setDepartmentFilter}
                options={departments}
                placeholder="All departments"
                ariaLabel="Filter by department"
              />
            </div>
            {/* <FieldFilter
              value={designationFilter}
              onChange={setDesignationFilter}
              options={designations}
              placeholder="All designations"
              ariaLabel="Filter by designation"
            /> */}
          </div>
        }
        toolbarEnd={
          // Rendered in DataTable's own right-aligned (ml-auto) container,
          // alongside the phone sort button, so it sits flush at the
          // toolbar's right edge on tablet/desktop. On phone it stays
          // inline with the filters/sort icon on the same row (rather than
          // forcing its own full-width row) by shortening its label to
          // "Add" - "Add users" only shows from sm up, where there's room.
          <Button size="sm" className="h-9 shrink-0" onClick={() => setAddOpen(true)}>
            <Plus className="h-3.5 w-3.5 sm:mr-1.5" />
            <span className="hidden sm:inline">Add users</span>
            <span className="sm:hidden">Add</span>
          </Button>
        }
      />

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(open) => {
          if (!open) {
            setConfirm(null);
            setConfirmError(null);
          }
        }}
        title={
          confirm?.grant
            ? `Grant ${roleLabel(confirm.role)} access?`
            : `Remove ${roleLabel(confirm?.role ?? "")} access?`
        }
        description={
          confirm?.grant
            ? `${confirm?.user.displayName} will gain ${roleLabel(confirm.role)} permissions immediately.`
            : `${confirm?.user.displayName} will lose ${roleLabel(confirm?.role ?? "")} permissions immediately.`
        }
        confirmLabel={confirm?.grant ? "Grant access" : "Remove access"}
        destructive={!confirm?.grant}
        pending={applying}
        onConfirm={applyConfirm}
      >
        {confirmError && (
          <p className="text-sm text-destructive">{confirmError}</p>
        )}
      </ConfirmDialog>

      <AddUsersDialog open={addOpen} onOpenChange={setAddOpen} onDone={load} />
    </PageContainer>
  );
}
